import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Settings
export const getSettings = async () => {
  const response = await axios.get(`${API}/settings`);
  return response.data;
};

export const updateSettings = async (settings) => {
  const response = await axios.put(`${API}/settings`, settings);
  return response.data;
};

export const uploadLogo = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/settings/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Promo Banners
export const getPromoBanners = async (activeOnly = true) => {
  const response = await axios.get(`${API}/promo-banners`, { params: { active_only: activeOnly } });
  return response.data;
};

export const createPromoBanner = async (banner) => {
  const response = await axios.post(`${API}/promo-banners`, banner);
  return response.data;
};

export const updatePromoBanner = async (bannerId, data) => {
  const response = await axios.put(`${API}/promo-banners/${bannerId}`, data);
  return response.data;
};

export const deletePromoBanner = async (bannerId) => {
  const response = await axios.delete(`${API}/promo-banners/${bannerId}`);
  return response.data;
};

export const uploadPromoBannerImage = async (bannerId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/promo-banners/${bannerId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Categories
export const getCategories = async (activeOnly = true) => {
  const response = await axios.get(`${API}/categories`, { params: { active_only: activeOnly } });
  return response.data;
};

export const createCategory = async (category) => {
  const response = await axios.post(`${API}/categories`, category);
  return response.data;
};

export const updateCategory = async (categoryId, data) => {
  const response = await axios.put(`${API}/categories/${categoryId}`, data);
  return response.data;
};

export const deleteCategory = async (categoryId, force = true) => {
  const response = await axios.delete(`${API}/categories/${categoryId}?force=${force}`);
  return response.data;
};

export const uploadCategoryImage = async (categoryId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/categories/${categoryId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Products
export const getProducts = async (categoryId = null, activeOnly = true) => {
  const params = { active_only: activeOnly };
  if (categoryId) params.category_id = categoryId;
  const response = await axios.get(`${API}/products`, { params });
  return response.data;
};

export const getProduct = async (productId) => {
  const response = await axios.get(`${API}/products/${productId}`);
  return response.data;
};

export const createProduct = async (product) => {
  const response = await axios.post(`${API}/products`, product);
  return response.data;
};

export const updateProduct = async (productId, data) => {
  const response = await axios.put(`${API}/products/${productId}`, data);
  return response.data;
};

export const deleteProduct = async (productId) => {
  const response = await axios.delete(`${API}/products/${productId}`);
  return response.data;
};

export const uploadProductImage = async (productId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/products/${productId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const uploadProductAdditionalImage = async (productId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/products/${productId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteProductAdditionalImage = async (productId, imageIndex) => {
  const response = await axios.delete(`${API}/products/${productId}/images/${imageIndex}`);
  return response.data;
};

export const updateProductImageAdjustment = async (productId, adjustment) => {
  const response = await axios.put(`${API}/products/${productId}/image-adjustment`, adjustment);
  return response.data;
};

export const updateProductAdditionalImageAdjustment = async (productId, imageIndex, adjustment) => {
  const response = await axios.put(`${API}/products/${productId}/images/${imageIndex}/adjustment`, adjustment);
  return response.data;
};

// Quotes
export const createQuote = async (cartData) => {
  const response = await axios.post(`${API}/quotes`, cartData);
  return response.data;
};

export const getQuotes = async () => {
  const response = await axios.get(`${API}/quotes`);
  return response.data;
};

export const getQuotePdf = async (quoteId) => {
  const response = await axios.get(`${API}/quotes/${quoteId}/pdf`, {
    responseType: 'blob'
  });
  return response.data;
};

export const generatePdfFromCart = async (cartData) => {
  const response = await axios.post(`${API}/quotes/generate-pdf`, cartData, {
    responseType: 'blob'
  });
  return response.data;
};

export const updateQuote = async (quoteId, data) => {
  const response = await axios.put(`${API}/quotes/${quoteId}`, data);
  return response.data;
};

export const deleteQuote = async (quoteId) => {
  const response = await axios.delete(`${API}/quotes/${quoteId}`);
  return response.data;
};

// Auth
export const adminLogin = async (username, password) => {
  const response = await axios.post(`${API}/auth/login`, { username, password });
  return response.data;
};

export const adminRegister = async (username, password, name) => {
  const response = await axios.post(`${API}/auth/register`, { username, password, name });
  return response.data;
};

// 2FA Functions
export const verify2FA = async (userId, code) => {
  const response = await axios.post(`${API}/auth/verify-2fa`, { user_id: userId, code });
  return response.data;
};

export const setup2FA = async (userId) => {
  const response = await axios.post(`${API}/auth/2fa/setup?user_id=${userId}`);
  return response.data;
};

export const enable2FA = async (userId, code) => {
  const response = await axios.post(`${API}/auth/2fa/enable`, { user_id: userId, code });
  return response.data;
};

export const disable2FA = async (userId, code) => {
  const response = await axios.post(`${API}/auth/2fa/disable`, { user_id: userId, code });
  return response.data;
};

export const get2FAStatus = async (userId) => {
  const response = await axios.get(`${API}/auth/2fa/status/${userId}`);
  return response.data;
};

export const changePassword = async (userId, currentPassword, newPassword) => {
  const response = await axios.put(`${API}/auth/change-password?user_id=${userId}`, {
    current_password: currentPassword,
    new_password: newPassword
  });
  return response.data;
};

export const getAdminUsers = async () => {
  const response = await axios.get(`${API}/auth/users`);
  return response.data;
};

export const deleteAdminUser = async (userId) => {
  const response = await axios.delete(`${API}/auth/users/${userId}`);
  return response.data;
};

export const updateUserPermissions = async (userId, permissions) => {
  const response = await axios.put(`${API}/auth/users/${userId}/permissions`, permissions);
  return response.data;
};

export const checkHasAdmin = async () => {
  const response = await axios.get(`${API}/auth/check`);
  return response.data;
};

// CRM
export const getCrmContacts = async (status = null, tag = null) => {
  const params = {};
  if (status) params.status = status;
  if (tag) params.tag = tag;
  const response = await axios.get(`${API}/crm/contacts`, { params });
  return response.data;
};

export const createCrmContact = async (contact) => {
  const response = await axios.post(`${API}/crm/contacts`, contact);
  return response.data;
};

export const updateCrmContact = async (contactId, data) => {
  const response = await axios.put(`${API}/crm/contacts/${contactId}`, data);
  return response.data;
};

export const deleteCrmContact = async (contactId) => {
  const response = await axios.delete(`${API}/crm/contacts/${contactId}`);
  return response.data;
};

export const addCrmNote = async (contactId, content, createdBy = null) => {
  const response = await axios.post(
    `${API}/crm/contacts/${contactId}/notes?content=${encodeURIComponent(content)}${createdBy ? `&created_by=${createdBy}` : ''}`
  );
  return response.data;
};

export const deleteCrmNote = async (contactId, noteId) => {
  const response = await axios.delete(`${API}/crm/contacts/${contactId}/notes/${noteId}`);
  return response.data;
};

// Mark contact as contacted today
export const markContactAsContacted = async (contactId) => {
  const response = await axios.post(`${API}/crm/contacts/${contactId}/contacted`);
  return response.data;
};

// CRM Tasks
export const addCrmTask = async (contactId, title, description = null, dueDate = null) => {
  let url = `${API}/crm/contacts/${contactId}/tasks?title=${encodeURIComponent(title)}`;
  if (description) url += `&description=${encodeURIComponent(description)}`;
  if (dueDate) url += `&due_date=${encodeURIComponent(dueDate)}`;
  const response = await axios.post(url);
  return response.data;
};

export const updateCrmTask = async (contactId, taskId, data) => {
  let url = `${API}/crm/contacts/${contactId}/tasks/${taskId}?`;
  if (data.completed !== undefined) url += `completed=${data.completed}&`;
  if (data.title) url += `title=${encodeURIComponent(data.title)}`;
  const response = await axios.put(url);
  return response.data;
};

export const deleteCrmTask = async (contactId, taskId) => {
  const response = await axios.delete(`${API}/crm/contacts/${contactId}/tasks/${taskId}`);
  return response.data;
};

export const importContactsFromQuotes = async () => {
  const response = await axios.post(`${API}/crm/import-from-quotes`);
  return response.data;
};

export const getCrmTags = async () => {
  const response = await axios.get(`${API}/crm/tags`);
  return response.data;
};

export const createCrmTag = async (name, color) => {
  const response = await axios.post(`${API}/crm/tags`, { name, color });
  return response.data;
};

export const deleteCrmTag = async (tagId) => {
  const response = await axios.delete(`${API}/crm/tags/${tagId}`);
  return response.data;
};

export const getCrmStats = async () => {
  const response = await axios.get(`${API}/crm/stats`);
  return response.data;
};

// CRM Pipeline (Customizable Statuses)
export const getCrmPipeline = async () => {
  const response = await axios.get(`${API}/crm/pipeline`);
  return response.data;
};

export const createPipelineStatus = async (status) => {
  const response = await axios.post(`${API}/crm/pipeline`, status);
  return response.data;
};

export const updatePipelineStatus = async (statusId, data) => {
  const response = await axios.put(`${API}/crm/pipeline/${statusId}`, data);
  return response.data;
};

export const deletePipelineStatus = async (statusId) => {
  const response = await axios.delete(`${API}/crm/pipeline/${statusId}`);
  return response.data;
};

export const reorderPipeline = async (statuses) => {
  const response = await axios.put(`${API}/crm/pipeline/reorder`, statuses);
  return response.data;
};


// ==================== TASKS ====================

export const getTasks = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
  if (filters.priority) params.append('priority', filters.priority);
  
  const response = await axios.get(`${API}/tasks?${params.toString()}`);
  return response.data;
};

export const getTask = async (taskId) => {
  const response = await axios.get(`${API}/tasks/${taskId}`);
  return response.data;
};

export const createTask = async (task) => {
  const response = await axios.post(`${API}/tasks`, task);
  return response.data;
};

export const updateTask = async (taskId, update) => {
  const response = await axios.put(`${API}/tasks/${taskId}`, update);
  return response.data;
};

export const deleteTask = async (taskId) => {
  const response = await axios.delete(`${API}/tasks/${taskId}`);
  return response.data;
};

export const addTaskComment = async (taskId, content, userId, userName) => {
  const response = await axios.post(`${API}/tasks/${taskId}/comments`, null, {
    params: { content, user_id: userId, user_name: userName }
  });
  return response.data;
};

export const getTasksStats = async () => {
  const response = await axios.get(`${API}/tasks/stats/summary`);
  return response.data;
};


// ==================== TASK STATUSES ====================

export const getTaskStatuses = async () => {
  const response = await axios.get(`${API}/task-statuses`);
  return response.data;
};

export const createTaskStatus = async (status) => {
  const response = await axios.post(`${API}/task-statuses`, status);
  return response.data;
};

export const updateTaskStatus = async (statusId, update) => {
  const response = await axios.put(`${API}/task-statuses/${statusId}`, update);
  return response.data;
};

export const deleteTaskStatus = async (statusId) => {
  const response = await axios.delete(`${API}/task-statuses/${statusId}`);
  return response.data;
};

export const reorderTaskStatuses = async (statuses) => {
  const response = await axios.put(`${API}/task-statuses/reorder`, statuses);
  return response.data;
};

// ==================== NOTIFICATIONS ====================

export const getNotifications = async (userId, unreadOnly = false) => {
  const response = await axios.get(`${API}/notifications/${userId}?unread_only=${unreadOnly}`);
  return response.data;
};

export const getUnreadCount = async (userId) => {
  const response = await axios.get(`${API}/notifications/${userId}/count`);
  return response.data;
};

export const markNotificationRead = async (notificationId) => {
  const response = await axios.put(`${API}/notifications/${notificationId}/read`);
  return response.data;
};

export const markAllNotificationsRead = async (userId) => {
  const response = await axios.put(`${API}/notifications/${userId}/read-all`);
  return response.data;
};

export const deleteNotification = async (notificationId) => {
  const response = await axios.delete(`${API}/notifications/${notificationId}`);
  return response.data;
};

export const checkTaskReminders = async () => {
  const response = await axios.post(`${API}/notifications/check-reminders`);
  return response.data;
};


// ==================== DATABASE BACKUP ====================

export const getBackupInfo = async () => {
  const response = await axios.get(`${API}/admin/backup/info`);
  return response.data;
};

export const exportDatabaseBackup = async () => {
  const response = await axios.get(`${API}/admin/backup/export`, {
    responseType: 'blob'
  });
  return response;
};

export const exportCompleteBackup = async () => {
  const response = await axios.get(`${API}/admin/backup/export-complete`, {
    responseType: 'blob'
  });
  return response;
};

export const previewBackupRestore = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/admin/backup/preview`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const restoreDatabaseBackup = async (file, mode = 'replace') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('mode', mode);
  const response = await axios.post(`${API}/admin/backup/restore`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Advanced Backup System
export const getBackupConfig = async () => {
  const response = await axios.get(`${API}/admin/backup/config`);
  return response.data;
};

export const updateBackupConfig = async (config) => {
  const response = await axios.put(`${API}/admin/backup/config`, config);
  return response.data;
};

export const getBackupRecords = async (limit = 20) => {
  const response = await axios.get(`${API}/admin/backup/records`, { params: { limit } });
  return response.data;
};

export const createManualBackup = async (includeFiles = true) => {
  const response = await axios.post(`${API}/admin/backup/create`, null, { 
    params: { include_files: includeFiles } 
  });
  return response.data;
};

export const downloadBackup = async (filename) => {
  const response = await axios.get(`${API}/admin/backup/download/${filename}`, {
    responseType: 'blob'
  });
  return response;
};

export const emailBackup = async (recordId, recipients = null) => {
  const response = await axios.post(`${API}/admin/backup/email/${recordId}`, recipients);
  return response.data;
};

export const deleteBackup = async (recordId) => {
  const response = await axios.delete(`${API}/admin/backup/${recordId}`);
  return response.data;
};

export const runBackupNow = async () => {
  const response = await axios.post(`${API}/admin/backup/run-now`);
  return response.data;
};

// Google Drive Backup
export const getDriveStatus = async () => {
  const response = await axios.get(`${API}/admin/drive/status`);
  return response.data;
};

export const getDriveConfig = async () => {
  const response = await axios.get(`${API}/admin/drive/config`);
  return response.data;
};

export const saveDriveConfig = async (clientId, clientSecret) => {
  const formData = new FormData();
  formData.append('client_id', clientId);
  formData.append('client_secret', clientSecret);
  const response = await axios.post(`${API}/admin/drive/config`, formData);
  return response.data;
};

export const connectDrive = async () => {
  const response = await axios.get(`${API}/admin/drive/connect`);
  return response.data;
};

export const disconnectDrive = async () => {
  const response = await axios.post(`${API}/admin/drive/disconnect`);
  return response.data;
};

export const uploadBackupToDrive = async (recordId) => {
  const response = await axios.post(`${API}/admin/backup/upload-drive/${recordId}`);
  return response.data;
};


// Gelatti Sync
export const requestGelattiSync = async () => {
  const response = await axios.post(`${API}/gelatti/request-sync`);
  return response.data;
};

export const getGelattiSyncStatus = async () => {
  const response = await axios.get(`${API}/gelatti/sync-status`);
  return response.data;
};

export const getGelattiServerStatus = async () => {
  const response = await axios.get(`${API}/gelatti/server-status`);
  return response.data;
};

// GraphQL Integration
export const testGraphQLConnection = async () => {
  const response = await axios.get(`${API}/integration/graphql/test`);
  return response.data;
};

export const previewGraphQLProducts = async () => {
  const response = await axios.get(`${API}/integration/graphql/preview`);
  return response.data;
};

export const syncGraphQLProducts = async () => {
  const response = await axios.post(`${API}/integration/graphql/sync`);
  return response.data;
};

export const importSelectedGraphQLProducts = async (productIds) => {
  const response = await axios.post(`${API}/integration/graphql/import-selected`, { product_ids: productIds });
  return response.data;
};

export const getGraphQLIntegrationStatus = async () => {
  const response = await axios.get(`${API}/integration/graphql/status`);
  return response.data;
};


// ==================== CEVADOR CONFIGURATOR ====================

export const getCevadorConfig = async () => {
  const response = await axios.get(`${API}/cevador/config`);
  return response.data;
};

export const updateCevadorConfig = async (config) => {
  const response = await axios.put(`${API}/cevador/config`, config);
  return response.data;
};

export const getCevadorModels = async (page = 1, limit = 24, category = null, search = null) => {
  const params = { page, limit };
  if (category && category !== 'all') params.category = category;
  if (search) params.search = search;
  const response = await axios.get(`${API}/cevador/models`, { params });
  return response.data;
};

export const getAllCevadorModels = async (page = 1, limit = 24, category = null) => {
  const params = { page, limit };
  if (category) params.category = category;
  const response = await axios.get(`${API}/cevador/models/all`, { params });
  return response.data;
};

export const getCevadorCategoryCounts = async () => {
  const response = await axios.get(`${API}/cevador/categories/counts`);
  return response.data;
};

export const getNextCevadorCode = async () => {
  const response = await axios.get(`${API}/cevador/models/next-code`);
  return response.data;
};

export const createCevadorModel = async (model) => {
  const response = await axios.post(`${API}/cevador/models`, model);
  return response.data;
};

export const updateCevadorModel = async (modelId, data) => {
  const response = await axios.put(`${API}/cevador/models/${modelId}`, data);
  return response.data;
};

export const deleteCevadorModel = async (modelId) => {
  const response = await axios.delete(`${API}/cevador/models/${modelId}`);
  return response.data;
};

export const uploadCevadorModelImage = async (modelId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/cevador/models/${modelId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const reorderCevadorModels = async (modelIds) => {
  const response = await axios.post(`${API}/cevador/models/reorder`, modelIds);
  return response.data;
};

export const uploadCevadorCustomImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/cevador/upload-custom-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};


// ==================== CUSTOMER (CLIENTE) ====================

export const registerCustomer = async (data) => {
  const response = await axios.post(`${API}/customer/register`, data);
  return response.data;
};

export const loginCustomer = async (data) => {
  // Convert phone to identifier for the new API format
  const loginData = {
    identifier: data.phone || data.identifier,
    password: data.password
  };
  const response = await axios.post(`${API}/customer/login`, loginData);
  return response.data;
};

export const getCustomerProfile = async (token) => {
  const response = await axios.get(`${API}/customer/me`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getCustomerOrders = async (token) => {
  const response = await axios.get(`${API}/customer/orders`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getCustomerOrderDetail = async (token, orderId) => {
  const response = await axios.get(`${API}/customer/orders/${orderId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const updateCustomerProfile = async (token, data) => {
  const response = await axios.put(`${API}/customer/me`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const logoutCustomer = async (token) => {
  const response = await axios.post(`${API}/customer/logout`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const verifyEmail = async (email, code) => {
  const response = await axios.post(`${API}/customer/verify-email`, { email, code });
  return response.data;
};

export const resendVerificationCode = async (email) => {
  const response = await axios.post(`${API}/customer/resend-verification`, { email });
  return response.data;
};

export const requestPasswordReset = async (email) => {
  const response = await axios.post(`${API}/customer/forgot-password`, { email });
  return response.data;
};

export const resetPassword = async (email, code, newPassword) => {
  const response = await axios.post(`${API}/customer/reset-password`, { 
    email, 
    code, 
    new_password: newPassword 
  });
  return response.data;
};

export const testSmtpConfiguration = async (toEmail) => {
  const response = await axios.post(`${API}/settings/test-smtp`, { to_email: toEmail });
  return response.data;
};


// ==================== CUSTOMER CART (PERSISTENT) ====================

export const getCustomerCart = async (token) => {
  const response = await axios.get(`${API}/customer/cart`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const saveCustomerCart = async (token, items) => {
  const response = await axios.put(`${API}/customer/cart`, { items }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const clearCustomerCart = async (token) => {
  const response = await axios.delete(`${API}/customer/cart`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};


// ==================== LOOKUP APIs ====================

export const lookupCep = async (cep) => {
  const response = await axios.get(`${API}/lookup/cep/${cep}`);
  return response.data;
};

export const lookupCnpj = async (cnpj) => {
  const response = await axios.get(`${API}/lookup/cnpj/${cnpj}`);
  return response.data;
};

export const validateCpfCnpj = async (value) => {
  const response = await axios.get(`${API}/validate/cpf-cnpj/${value}`);
  return response.data;
};

export const getQuotesWithCustomers = async () => {
  const response = await axios.get(`${API}/quotes-with-customers`);
  return response.data;
};

// Admin Customer Management
export const getAdminCustomers = async () => {
  const response = await axios.get(`${API}/admin/customers`);
  return response.data;
};

export const deleteAdminCustomer = async (customerId) => {
  const response = await axios.delete(`${API}/admin/customers/${customerId}`);
  return response.data;
};

export const getAdminCustomerDetails = async (customerId) => {
  const response = await axios.get(`${API}/admin/customers/${customerId}`);
  return response.data;
};


// Landing Page
export const getLandingConfig = async () => {
  const response = await axios.get(`${API}/landing/cevadores`);
  return response.data;
};

export const updateLandingConfig = async (config) => {
  const response = await axios.put(`${API}/landing/cevadores`, config);
  return response.data;
};

export const uploadLandingGalleryImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/landing/cevadores/gallery`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteLandingGalleryImage = async (imageId) => {
  const response = await axios.delete(`${API}/landing/cevadores/gallery/${imageId}`);
  return response.data;
};

export const uploadLandingVideo = async (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/landing/cevadores/video`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    }
  });
  return response.data;
};

export const deleteLandingVideo = async () => {
  const response = await axios.delete(`${API}/landing/cevadores/video`);
  return response.data;
};
